import { Fragment } from "react";
import { debounce } from "lodash-es";
import { Link as RouterLink } from "react-router-dom";
import {
  alpha,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { ContentFlag } from "~/components/content-flag";
import { FormatRelativeDate } from "~/lib/date";
import { ActionMenu } from "~/components/action-menu";
import { AwsIntegration } from "./AwsIntegrationHostedPage";
import { CanDelete } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Delete";
import { CanUpdate } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Update";
import {
  CanTriggerAction,
  useCanTriggerAction,
} from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/TriggerAction";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { formatStatus } from "../../hosted-integrations/utils/statuses";

interface AwsAccountHeaderProps {
  scope: SpaceScope | OrganizationScope;
  integrationId: string;
  awsIntegration: AwsIntegration;
  actions: {
    triggerScan: () => void;
    ping: () => void;
    deleteIntegration: () => void;
  };
  mrn: string;
}

export const AwsIntegrationHostedHeader = ({
  awsIntegration,
  mrn,
  actions,
  scope,
  integrationId,
}: AwsAccountHeaderProps) => {
  const hasIntegrationTriggerActionPermission = useCanTriggerAction();

  const agentCheckinDate = (timestamp: string | null) => {
    if (timestamp == null || timestamp == "") {
      return "Never";
    }
    return FormatRelativeDate(timestamp);
  };

  const showTrigger =
    (awsIntegration.status === "ACTIVE" ||
      awsIntegration.status === "WARNING" ||
      awsIntegration.status === "ERROR") &&
    hasIntegrationTriggerActionPermission;

  return (
    <Box>
      <Grid container>
        <Grid
          size={{
            xs: 12,
            md: "grow",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              id="aws-integration-name"
              variant="h4"
              fontWeight={700}
              sx={{ display: "inline", pr: 2, overflowWrap: "anywhere" }}
            >
              {awsIntegration.name}
            </Typography>
            <ContentFlag
              flag={formatStatus(awsIntegration.status)}
              color={awsIntegration.status}
            />
          </Box>
          <Grid container columnGap={2}>
            <Grid
              size={{
                xs: 12,
                sm: "auto",
              }}
            >
              <AccessTimeIcon
                sx={{
                  fontSize: "inherit",
                  color: "text.secondary",
                  verticalAlign: "middle",
                }}
              />
              {awsIntegration.lastCheckin && (
                <Typography
                  display="inline"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 0.75 }}
                >
                  Last scanned: {agentCheckinDate(awsIntegration.lastCheckin)}
                </Typography>
              )}
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: "auto",
              }}
            >
              {awsIntegration.createdBy?.name != "" && (
                <Fragment>
                  <PersonIcon
                    sx={{
                      fontSize: "inherit",
                      color: "text.secondary",
                      verticalAlign: "middle",
                    }}
                  />
                  <Typography
                    display="inline"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 0.75 }}
                  >
                    Created by: {awsIntegration.createdBy?.name}
                  </Typography>
                </Fragment>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{
            color: "text.secondary",
            mb: 1.625,
          }}
          size={{
            xs: 12,
            md: 5,
          }}
        >
          <Grid
            display="flex"
            alignItems="center"
            justifyContent="end"
            size="grow"
          >
            <CanUpdate>
              <IconButton
                size="large"
                component={RouterLink}
                to={`/space/integrations/aws/${integrationId}/edit?${scope.params}&integrationMrn=${awsIntegration.mrn}`}
                sx={{ mx: 0.5 }}
              >
                <EditIcon />
              </IconButton>
            </CanUpdate>
            <CanDelete>
              <IconButton
                size="large"
                sx={{
                  mx: 0.5,
                  color: (theme) => theme.palette.error.main,
                  "&:hover": {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.error.main, 0.15),
                  },
                }}
                onClick={actions.deleteIntegration}
              >
                <DeleteIcon />
              </IconButton>
            </CanDelete>
            <CanTriggerAction>
              <ActionMenu
                id="aws-account-actions"
                actions={[
                  {
                    label: "Copy MRN",
                    action: () => navigator.clipboard.writeText(`${mrn}`),
                    dataName: "aws-copyMrn",
                  },
                ]}
              />
            </CanTriggerAction>
            {/* Scan Trigger only visible when user has permission and status is active/error/warning */}
            {showTrigger && (
              <Fragment>
                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={debounce(actions.triggerScan, 1000)}
                  data-name="aws-triggerscan"
                  sx={{
                    position: "relative",
                    background:
                      "linear-gradient(133.55deg, #9147FF 0%, #4C35E8 100%)", //<--- palette breaking
                    ml: 1,
                  }}
                >
                  Scan Now
                </Button>
              </Fragment>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
